import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import SegmentedControl from './SegmentedControl';
import VEITLogo from './VEITLogo';
import { FiChevronRight, FiChevronLeft, FiShield, FiUser, FiBriefcase, FiUsers, FiFileText } from 'react-icons/fi';

const DOMAINS = [
  { id: 'info', title: 'Client Information', subtitle: 'Tell us about the organization', icon: FiFileText, color: 'from-veit-dark to-veit-primary' },
  { id: 'd1', title: 'Identity & Access Management', subtitle: 'Domain 1 of 6', icon: FiShield, color: 'from-blue-600 to-blue-800',
    questions: [
      { key: 'q1', label: 'Q1. Do you use multi-factor authentication (MFA) on email, remote access, and admin accounts?', options: ['Yes', 'Partial', 'No'] },
      { key: 'q2', label: 'Q2. Do employees share passwords or use the same password across multiple systems?', options: ['No', 'Unknown', 'Yes'] },
    ]
  },
  { id: 'd2', title: 'Endpoint & Network Security', subtitle: 'Domain 2 of 6', icon: FiShield, color: 'from-cyan-600 to-blue-700',
    questions: [
      { key: 'q3', label: 'Q3. Are all company devices running active endpoint protection software (antivirus / EDR)?', options: ['Yes', 'Partial', 'No'] },
      { key: 'q4', label: 'Q4. Do you have a firewall in place, and has it been reviewed/updated in the last 12 months?', options: ['Yes', 'No', 'Unknown'] },
    ]
  },
  { id: 'd3', title: 'Data Backup & Recovery', subtitle: 'Domain 3 of 6', icon: FiShield, color: 'from-emerald-600 to-teal-700',
    questions: [
      { key: 'q5', label: 'Q5. Do you have automated, offsite or cloud backups running for critical business data?', options: ['Yes', 'Partial', 'No'] },
      { key: 'q6', label: 'Q6. Have you tested your backup recovery process in the last 6 months?', options: ['Yes', 'No', 'Never Tested'] },
    ]
  },
  { id: 'd4', title: 'Email & Phishing Risk', subtitle: 'Domain 4 of 6', icon: FiShield, color: 'from-amber-500 to-orange-600',
    questions: [
      { key: 'q7', label: 'Q7. Have you experienced a phishing attempt or business email compromise in the last 12 months?', options: ['Yes', 'No', 'Unsure'] },
      { key: 'q8', label: 'Q8. Do you have email security filtering (spam filtering, anti-phishing) in place?', options: ['Yes', 'No', 'Unknown'] },
    ]
  },
  { id: 'd5', title: 'Security Awareness & Policy', subtitle: 'Domain 5 of 6', icon: FiShield, color: 'from-rose-600 to-pink-700',
    questions: [
      { key: 'q9', label: 'Q9. Do your employees receive any cybersecurity awareness training?', options: ['Yes — Ongoing', 'Yes — Annually', 'No'] },
      { key: 'q10', label: 'Q10. Do you have a written security policy or acceptable use policy (AUP)?', options: ['Yes', 'Informal/Undocumented', 'No'] },
    ]
  },
  { id: 'd6', title: 'Compliance & Insurance', subtitle: 'Domain 6 of 6', icon: FiShield, color: 'from-indigo-600 to-violet-700',
    questions: [
      { key: 'q11', label: 'Q11. Do you carry cyber liability insurance, and can you meet carrier requirements?', options: ['Yes, Confident', 'Yes, Unsure', 'No Insurance'] },
      { key: 'q12', label: 'Q12. Are you subject to any compliance frameworks (HIPAA, PCI-DSS, CMMC, SOC2, etc)?', options: ['Yes', 'No', 'Unsure'] },
    ]
  },
];

const IntakeForm = ({ initialData, onGenerate }) => {
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState(initialData);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const domain = DOMAINS[step];
  const totalSteps = DOMAINS.length;

  const isInfoComplete = ['companyName', 'industry', 'employeeCount', 'primaryContact'].every(k => formData[k]?.trim());
  const isDomainComplete = step === 0 ? isInfoComplete : domain.questions?.every(q => formData[q.key]);
  const isLastStep = step === totalSteps - 1;
  const progress = ((step) / (totalSteps - 1)) * 100;

  const handleNext = () => {
    if (isLastStep) {
      onGenerate(formData);
    } else setStep(s => s + 1);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-veit-navy via-slate-900 to-veit-navy flex flex-col">
      {/* Top Bar */}
      <div className="flex items-center justify-between px-8 py-5 border-b border-white/10">
        <VEITLogo light />
        <div className="flex flex-col items-end">
          <div className="text-sm text-white font-black tracking-widest uppercase">
            Discovery Assessment
          </div>
          <div className="text-[10px] text-veit-highlight/80 font-medium tracking-wide">
            Discover your Threat Landscape
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="px-8 pt-6">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-veit-highlight uppercase tracking-widest">
              {step === 0 ? 'Getting Started' : `Step ${step} of ${totalSteps - 1}`}
            </span>
            <span className="text-xs text-slate-400">{Math.round(progress)}% complete</span>
          </div>
          <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-veit-dark to-veit-primary rounded-full transition-all duration-500" 
                 style={{ width: `${progress}%` }} />
          </div>
          {/* Step Pills */}
          <div className="flex gap-1.5 mt-3">
            {DOMAINS.map((d, i) => (
              <div key={d.id} className={`flex-1 h-1 rounded-full transition-all duration-300 ${
                i < step ? 'bg-veit-primary' : i === step ? 'bg-veit-secondary' : 'bg-white/10'
              }`} />
            ))}
          </div>
        </div>
      </div>

      {/* Card */}
      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-2xl">
          {/* Domain Header */}
          <div className={`bg-gradient-to-r ${domain.color} rounded-t-2xl px-8 py-6`}>
            <p className="text-white/80 text-xs font-bold uppercase tracking-widest mb-1">{domain.subtitle}</p>
            <h2 className="text-2xl font-black text-white">{domain.title}</h2>
          </div>

          {/* Card Body */}
          <div className="bg-white rounded-b-2xl shadow-2xl px-8 py-8">
            {step === 0 ? (
              <InfoStep formData={formData} onChange={handleChange} />
            ) : (
              <div className="space-y-8">
                {domain.questions.map((q) => (
                  <div key={q.key}>
                    <label className="block text-sm font-semibold text-veit-navy mb-3 leading-snug">{q.label}</label>
                    <SegmentedControl name={q.key} options={q.options} value={formData[q.key]} onChange={handleChange} />
                  </div>
                ))}
              </div>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between mt-10 pt-6 border-t border-slate-100">
              <button 
                type="button" 
                onClick={() => setStep(s => Math.max(0, s - 1))}
                disabled={step === 0}
                className="flex items-center gap-2 text-sm font-semibold text-slate-400 hover:text-veit-navy disabled:opacity-0 transition-colors"
              >
                <SafeIcon icon={FiChevronLeft} /> Back
              </button>

              <button 
                type="button" 
                onClick={handleNext}
                disabled={!isDomainComplete}
                className={`flex items-center gap-2 px-7 py-3 rounded-xl text-sm font-bold shadow-lg transition-all duration-200 ${
                  isDomainComplete 
                    ? 'bg-veit-secondary hover:bg-veit-primary text-white hover:-translate-y-0.5 hover:shadow-veit-secondary/30' 
                    : 'bg-veit-light text-slate-400 cursor-not-allowed'
                }`}
              >
                {isLastStep ? 'Generate Report' : 'Continue'} <SafeIcon icon={FiChevronRight} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const InfoStep = ({ formData, onChange }) => {
  const fields = [
    { label: 'Company Name', name: 'companyName', placeholder: 'Acme Corp', icon: FiBriefcase },
    { label: 'Industry / Vertical', name: 'industry', placeholder: 'Manufacturing, Healthcare, etc.', icon: FiFileText },
    { label: 'Employee Count', name: 'employeeCount', placeholder: 'e.g., 50–100', icon: FiUsers },
    { label: 'Primary Contact', name: 'primaryContact', placeholder: 'John Doe, CEO', icon: FiUser },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
      {fields.map(({ label, name, placeholder, icon }) => (
        <div key={name}>
          <label className="block text-sm font-semibold text-veit-navy mb-1.5">{label}</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
              <SafeIcon icon={icon} className="text-slate-400 text-sm" />
            </div>
            <input 
              type="text" 
              name={name} 
              value={formData[name]} 
              onChange={onChange}
              required
              placeholder={placeholder}
              className="pl-9 w-full border border-veit-border bg-veit-light rounded-lg py-2.5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-veit-secondary focus:border-transparent transition-all text-veit-navy"
            />
          </div>
        </div>
      ))}
    </div>
  );
};

export default IntakeForm;